import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  Chip,
  Autocomplete,
  Snackbar,
  Alert,
  Stack,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Link as LinkIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  List as ListIcon,
  Search as SearchIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { ProfileDateTimePicker } from 'components/social/profile/ProfileDialogs';

const Sidebar = styled(Paper)(({ theme }) => ({
  width: '100%',
  minWidth: 0,
  // as tall as its content, up to the space it is given (the list scrolls beyond that)
  maxHeight: '100%',
  minHeight: 0,
  borderRadius: theme.shape.borderRadius,
  borderRight: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
}));

/**
 * QuickAccessPanel Component
 * 
 * Provides a sidebar with:
 * - Quick jump dropdowns for agencies, programs, and calls
 * - Pinned quick links
 * - Submissions list
 * - Create call button
 * 
 * @param {Object} props
 * @param {Array} props.links - Array of pinned quick links
 * @param {Function} props.onRemove - Handler for removing a link
 * @param {Function} props.onEdit - Handler for editing a link
 * @param {Function} props.onOpen - Handler for opening/clicking a link
 * @param {Function} props.onAddCustom - Handler for adding custom link
 * @param {Object} props.catalog - Object containing agencies, programs, and calls arrays
 * @param {Function} props.onJumpAgency - Handler when agency is selected from dropdown
 * @param {Function} props.onJumpProgram - Handler when program is selected from dropdown
 * @param {Function} props.onJumpCall - Handler when call is selected from dropdown
 * @param {Function} props.onCreateAgency - Handler for creating new agency
 * @param {Function} props.onCreateProgram - Handler for creating new program
 * @param {Function} props.onCreateCall - Handler for creating new call
 * @param {Array} props.contributions - Array of user submissions
 */


// Matches backend AGENCY_CATEGORIES choices
const AGENCY_CATEGORIES = [
  { code: 'NAT', label: 'Government Funding' },
  { code: 'INT', label: 'International & Multilateral Organizations' },
  { code: 'PRV', label: 'Private Foundations and Corporations' },
  { code: 'IND', label: 'Industry and Corporate Funding' },
  { code: 'UNI', label: 'University-Based Funding' },
  { code: 'PVT', label: 'Private Associations and Foundations' },
  { code: 'VCT', label: 'Venture Capital and Startup Ecosystems' },
  { code: 'NGO', label: 'Non-Governmental Organizations (NGOs)' },
  { code: 'PER', label: 'Personal or Legacy Philanthropic Support' },
  { code: 'COM', label: 'Community or Local Associations' },
  { code: 'CFD', label: 'Crowdfunding Platforms' },
];

const COOPERATION_TYPES = [
  { code: 'NAT', label: 'National' },
  { code: 'BIL', label: 'Bilateral' },
  { code: 'INT', label: 'International / Multilateral' },
];

const COUNTRY_OPTIONS = [
  'International',
  'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Australia', 'Austria',
  'Belgium', 'Brazil', 'Canada', 'Chile', 'China', 'Colombia', 'Croatia',
  'Czech Republic', 'Denmark', 'Egypt', 'European Union', 'Finland', 'France',
  'Germany', 'Greece', 'Hungary', 'India', 'Indonesia', 'Iran', 'Ireland',
  'Israel', 'Italy', 'Japan', 'Jordan', 'Kenya', 'South Korea', 'Luxembourg',
  'Malaysia', 'Mexico', 'Morocco', 'Netherlands', 'New Zealand', 'Nigeria',
  'Norway', 'Pakistan', 'Philippines', 'Poland', 'Portugal', 'Romania',
  'Russia', 'Saudi Arabia', 'Singapore', 'Slovakia', 'Slovenia', 'South Africa',
  'Spain', 'Sweden', 'Switzerland', 'Taiwan', 'Thailand', 'Türkiye', 'Ukraine',
  'United Arab Emirates', 'United Kingdom', 'United States', 'Vietnam',
];

/***************************************************
 * Contribution Dialog (Create Agency/Program/Call)
 ***************************************************/
function ContributionDialog({ open, kind, onClose, onSubmit, catalog }) {
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ 
    // Common fields
    title: '', 
    code: '',
    url: '',
    
    // Agency fields
    country: '',
    website: '',
    category: '',
    
    // Program fields
    agencyId: '',
    coopType: '',
    description: '',
    trlRange: '',
    recurring: false,
    
    // Call fields
    programId: '',
    scope: '',
    expectedOutcomes: '',
    eligibilityCriteria: '',
    typeOfAction: 'research',
    opensAt: '',
    closesAt: '',
    deadline2: '',
    hasStage2: false,
    timezone: 'UTC',
    totalBudget: '',
    maxPerProject: '',
    minPartners: '',
    submissionUrl: '',
    status: 'upcoming',
    dataSourceUrl: '',
    alwaysOpen: false,
    callYear: '',
    callTerm: '',
    callSequence: '',
    termEnabled: false,
    sequenceEnabled: false,

    // Nested creation flags
    newAgencyName: '',
    newAgencyCountry: '',
    newAgencyCategory: '',
    newProgramName: '',
    isNewAgency: false,
    isNewProgram: false,
  });

  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      const defaultForm = {
        title: '', 
        code: '',
        url: '',
        country: '',
        website: '',
        category: '',
        agencyId: '',
        coopType: '',
        description: '',
        trlRange: '',
        recurring: false,
        programId: '',
        scope: '',
        expectedOutcomes: '',
        eligibilityCriteria: '',
        typeOfAction: 'research',
        opensAt: '',
        closesAt: '',
        deadline2: '',
        hasStage2: false,
        timezone: 'UTC',
        totalBudget: '',
        maxPerProject: '',
        minPartners: '',
        submissionUrl: '',
        status: 'upcoming',
        newAgencyName: '',
        newAgencyCountry: '',
        newAgencyCategory: '',
        newProgramName: '',
        isNewAgency: false,
        isNewProgram: false,
        dataSourceUrl: '',
        alwaysOpen: false,
        callYear: '',
        callTerm: '',
        callSequence: '',
        termEnabled: false,
        sequenceEnabled: false,
      };
      setForm(defaultForm);
    }
  }, [open, kind]);

  const set = (k) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: value }));
  };

  const agencyOptions = useMemo(() => [
    { id: '__new__', name: '+ Create New Agency' },
    ...(catalog?.agencies || [])
  ], [catalog?.agencies]);
  
  const programOptions = useMemo(() => [
    { id: '__new__', name: '+ Create New Program' },
    ...(catalog?.programs || []).filter((p) => !form.agencyId || form.agencyId === '__new__' || p.agencyId === form.agencyId)
  ], [catalog?.programs, form.agencyId]);

  const handleSubmit = async () => {
    try {
      setError(null);

      let submitData = { kind };

      if (kind === 'agency') {
        Object.assign(submitData, {
          name: form.title,
          country: form.country,
          website: form.website || null,
          category: form.category || null,
        });
      } else if (kind === 'program') {
        Object.assign(submitData, {
          name: form.title,
          code: form.code || form.title.replace(/\s+/g, '-'),
          agency: form.agencyId,
          coop_type: form.coopType || null,
          description: form.description || null,
          trl_range: form.trlRange || null,
          recurring: form.recurring || false,
          is_new_agency: form.isNewAgency,
          new_agency_name: form.newAgencyName,
          new_agency_country: form.newAgencyCountry,
          new_agency_category: form.newAgencyCategory,
        });
      } else if (kind === 'call') {
        const code = form.code || form.title.replace(/\s+/g, '-');
        const is_always_open = form.alwaysOpen;
        const opens_at = is_always_open
          ? null
          : form.opensAt
            ? (form.opensAt.includes('T') ? form.opensAt : form.opensAt + ':00')
            : null;
        const closes_at = is_always_open
          ? null
          : form.closesAt
            ? (form.closesAt.includes('T') ? form.closesAt : form.closesAt + ':00')
            : null;
        const deadline2 = (!is_always_open && form.hasStage2 && form.deadline2)
          ? (form.deadline2.includes('T') ? form.deadline2 : form.deadline2 + ':00')
          : null;

        Object.assign(submitData, {
          program: form.programId,
          code,
          title: form.title,
          description: form.description || null,
          scope: form.scope || null,
          expected_outcomes: form.expectedOutcomes || null,
          eligibility_criteria: form.eligibilityCriteria || null,
          type_of_action: form.typeOfAction || 'research',
          opens_at,
          closes_at,
          deadline2,
          is_always_open,
          call_year: form.callYear ? parseInt(form.callYear, 10) : null,
          call_term: form.callTerm ? parseInt(form.callTerm, 10) : null,
          call_sequence: form.callSequence ? parseInt(form.callSequence, 10) : null,
          timezone: form.timezone || 'UTC',
          total_budget: form.totalBudget || null,
          max_per_project: form.maxPerProject || null,
          min_partners: form.minPartners || null,
          submission_url: form.submissionUrl || null,
          external_url: form.dataSourceUrl || null,
          status: is_always_open ? 'open' : (form.status || 'upcoming'),
          agency: form.agencyId,
          is_new_agency: form.isNewAgency,
          new_agency_name: form.newAgencyName,
          new_agency_country: form.newAgencyCountry,
          new_agency_category: form.newAgencyCategory,
          is_new_program: form.isNewProgram,
          new_program_name: form.newProgramName,
        });
      }

      await onSubmit?.(submitData);
      onClose();
    } catch (err) {
      const data = err?.response?.data;
      let errorMessage;
      if (data) {
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.message) {
          errorMessage = data.message;
        } else if (data.error) {
          errorMessage = data.error;
        } else {
          // Field-level DRF validation errors: { field: ["msg", ...], ... }
          errorMessage = Object.entries(data)
            .map(([field, msgs]) => `${Array.isArray(msgs) ? msgs.join(' ') : msgs}`)
            .join('\n');
        }
      }
      setError(errorMessage || err?.message || `Failed to create ${kind}.`);
    }
  };



  const options = {
    'research': 'Research',
    'innovation': 'Innovation',
    'ria': 'Research and Innovation',
    'csa': 'Coordination and Support Action',
    'cofund': 'Co-Fund Action',
    'ppi': 'Public Procurement of Innovation',
  }

  const actionOptions = Object.entries(options).map(([key, label]) => ({
                  key,
                  label
                }));

  // Validation
  const isValid = () => {
    if (!form.title) return false;
    if (kind === 'agency') {
      return form.country && form.category;
    }
    if (kind === 'program') {
      if (form.isNewAgency && (!form.newAgencyName || !form.newAgencyCountry)) return false;
      return form.agencyId;
    }
    if (kind === 'call') {
      if (form.isNewAgency && (!form.newAgencyName || !form.newAgencyCountry)) return false;
      if (form.isNewProgram && !form.newProgramName) return false;
      if (form.alwaysOpen && !form.callYear) return false;
      return form.programId && ((form.opensAt && form.closesAt) || form.alwaysOpen) && form.dataSourceUrl;
    }
    return true;
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={kind === 'call' ? 'md' : 'sm'}
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
      sx={{  zIndex: 3000  }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: kind === 'call' ? 0 : undefined }}>
        {kind === 'agency' ? 'Create New Agency' : kind === 'program' ? 'Create New Program' : 'Create New Call'}
        {kind === 'call' && (
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, mt: 0.5 }}>
            All fields marked with * are required
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>

      {/* AGENCY FORM */}
      {kind === 'agency' && (
        <>
          <TextField
            size="small"
            label="Agency Name *"
            fullWidth
            sx={{ mb: 1.5 }}
            value={form.title}
            onChange={set('title')}
          />
          <Autocomplete
            size="small"
            freeSolo
            options={COUNTRY_OPTIONS}
            value={form.country}
            onChange={(_, v) => setForm(f => ({ ...f, country: v || '' }))}
            onInputChange={(_, v) => setForm(f => ({ ...f, country: v }))}
            renderInput={(params) => (
              <TextField {...params} label="Country *" fullWidth sx={{ mb: 1.5 }} />
            )}
          />
          <TextField
            size="small"
            label="Website (optional)"
            fullWidth
            sx={{ mb: 1.5 }}
            value={form.website}
            onChange={set('website')}
            placeholder="https://..."
          />
          <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
            <InputLabel>Category *</InputLabel>
            <Select
              label="Category *"
              value={form.category}
              onChange={set('category')}
            >
              {AGENCY_CATEGORIES.map(({ code, label }) => (
                <MenuItem key={code} value={code}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}

      {/* PROGRAM FORM */}
      {kind === 'program' && (
        <>
          <TextField
            size="small"
            label="Program Name *"
            fullWidth
            sx={{ mb: 1.5 }}
            value={form.title}
            onChange={set('title')}
          />
          <TextField
            size="small"
            label="Program Code"
            fullWidth
            sx={{ mb: 1.5 }}
            value={form.code}
            onChange={set('code')}
            placeholder="e.g., R01, ERC-STG"
          />

          <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
            <InputLabel>Cooperation Type</InputLabel>
            <Select
              label="Cooperation Type"
              value={form.coopType}
              onChange={set('coopType')}
            >
              <MenuItem value=""><em>Not specified</em></MenuItem>
              {COOPERATION_TYPES.map(({ code, label }) => (
                <MenuItem key={code} value={code}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            size="small"
            options={agencyOptions}
            getOptionLabel={(o) => o?.name || ''}
            value={agencyOptions.find((a) => a.id === form.agencyId) || null}
            onChange={(_, v) => {
              const isNew = v?.id === '__new__';
              setForm((f) => ({ 
                ...f, 
                agencyId: v?.id || '', 
                isNewAgency: isNew,
              }));
            }}
            renderInput={(params) => <TextField {...params} label="Agency *" sx={{ mb: 1.5 }} />}
          />
          
          {form.isNewAgency && (
            <Box sx={{ pl: 2, mb: 1.5, borderLeft: (t) => `3px solid ${t.palette.primary.main}` }}>
              <TextField
                size="small"
                label="New Agency Name *"
                fullWidth
                sx={{ mb: 1.5 }}
                value={form.newAgencyName}
                onChange={set('newAgencyName')}
              />
              <Autocomplete
                size="small"
                freeSolo
                options={COUNTRY_OPTIONS}
                value={form.newAgencyCountry}
                onChange={(_, v) => setForm(f => ({ ...f, newAgencyCountry: v || '' }))}
                onInputChange={(_, v) => setForm(f => ({ ...f, newAgencyCountry: v }))}
                renderInput={(params) => (
                  <TextField {...params} label="New Agency Country *" fullWidth sx={{ mb: 1.5 }} />
                )}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={form.newAgencyCategory}
                  onChange={set('newAgencyCategory')}
                >
                  {AGENCY_CATEGORIES.map(({ code, label }) => (
                    <MenuItem key={code} value={code}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <TextField
            size="small"
            label="Description (optional)"
            fullWidth
            multiline
            rows={2}
            sx={{ mb: 1.5 }}
            value={form.description}
            onChange={set('description')}
          />
          <TextField
            size="small"
            label="URL (optional)"
            fullWidth
            sx={{ mb: 1.5 }}
            value={form.url}
            onChange={set('url')}
          />
        </>
      )}

      {/* CALL FORM */}
      {kind === 'call' && (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {/* Basic Information */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1 }}>Basic Information</Typography>
          
          <TextField
            size="small"
            label="Call Title *"
            fullWidth
            value={form.title}
            onChange={set('title')}
          />
          <TextField
            size="small"
            label="Call Code (Optional)"
            fullWidth
            value={form.code || form.title.replace(' ', '-')}
            onChange={set('code')}
            placeholder="e.g., R01-2026-001"
          />

          <TextField
            size="small"
            label="Data Source URL *"
            fullWidth
            value={form.dataSourceUrl}
            onChange={set('dataSourceUrl')}
            placeholder="https://..."
          />


          {/* Program & Agency Selection */}
          <Autocomplete
            size="small"
            options={agencyOptions}
            getOptionLabel={(o) => o?.name || ''}
            value={agencyOptions.find((a) => a.id === form.agencyId) || null}
            onChange={(_, v) => {
              const isNew = v?.id === '__new__';
              setForm((f) => ({ 
                ...f, 
                agencyId: v?.id || '', 
                isNewAgency: isNew,
                programId: '', // Reset program when agency changes
              }));
            }}
            renderInput={(params) => <TextField {...params} label="Agency" />}
          />
          
          {form.isNewAgency && (
            <Box sx={{ pl: 2, borderLeft: (t) => `3px solid ${t.palette.primary.main}` }}>
              <TextField
                size="small"
                label="New Agency Name *"
                fullWidth
                sx={{ mb: 1.5 }}
                value={form.newAgencyName}
                onChange={set('newAgencyName')}
              />
              <Autocomplete
                size="small"
                freeSolo
                options={COUNTRY_OPTIONS}
                value={form.newAgencyCountry}
                onChange={(_, v) => setForm(f => ({ ...f, newAgencyCountry: v || '' }))}
                onInputChange={(_, v) => setForm(f => ({ ...f, newAgencyCountry: v }))}
                renderInput={(params) => (
                  <TextField {...params} label="New Agency Country *" fullWidth sx={{ mb: 1.5 }} />
                )}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={form.newAgencyCategory}
                  onChange={set('newAgencyCategory')}
                >
                  {AGENCY_CATEGORIES.map(({ code, label }) => (
                    <MenuItem key={code} value={code}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Autocomplete
            size="small"
            options={programOptions}
            getOptionLabel={(o) => o?.name || ''}
            value={programOptions.find((p) => p.id === form.programId) || null}
            onChange={(_, v) => {
              const isNew = v?.id === '__new__';
              setForm((f) => ({ 
                ...f, 
                programId: v?.id || '',
                isNewProgram: isNew,
              }));
            }}
            renderInput={(params) => <TextField {...params} label="Program *" />}
            disabled={!form.agencyId && !form.isNewAgency}
          />
          
          {form.isNewProgram && (
            <Box sx={{ pl: 2, borderLeft: (t) => `3px solid ${t.palette.primary.main}` }}>
              <TextField
                size="small"
                label="New Program Name *"
                fullWidth
                value={form.newProgramName}
                onChange={set('newProgramName')}
                placeholder="Enter program name"
              />
            </Box>
          )}

          {/* Dates */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1 }}>Timeline</Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={form.alwaysOpen}
                  onChange={(e) => {
                    set('alwaysOpen')(e.target.checked);
                    set('status')(e.target.checked ? 'open' : 'upcoming');
                    if (e.target.checked) setForm(f => ({ ...f, hasStage2: false, deadline2: '' }));
                  }}
                />
              }
              label="Always Open"
              sx={{ mr: 0 }}
            />
            <TextField
              size="small"
              label={form.alwaysOpen ? 'Year *' : 'Year'}
              type="number"
              value={form.callYear}
              onChange={set('callYear')}
              placeholder="2026"
              error={form.alwaysOpen && !form.callYear}
              // helperText={form.alwaysOpen ? '' : 'optional'}
              inputProps={{ min: 2000, max: 2099, style: { width: 100 } }}
              sx={{ width: 96 }}
            />
            <Box
              title={form.termEnabled ? undefined : 'Click to enable'}
              onClick={() => !form.termEnabled && setForm(f => ({ ...f, termEnabled: true }))}
              sx={{ cursor: form.termEnabled ? 'default' : 'pointer' }}
            >
              <TextField
                size="small"
                label="Term"
                type="number"
                value={form.callTerm}
                onChange={set('callTerm')}
                placeholder="1"
                disabled={!form.termEnabled}
                inputProps={{ min: 1, max: 9, style: { width: 100 } }}
                sx={{ width: 120, pointerEvents: form.termEnabled ? 'auto' : 'none' }}
              />
            </Box>
            <Box
              title={form.sequenceEnabled ? undefined : 'Click to enable'}
              onClick={() => !form.sequenceEnabled && setForm(f => ({ ...f, sequenceEnabled: true }))}
              sx={{ cursor: form.sequenceEnabled ? 'default' : 'pointer' }}
            >
              <TextField
                size="small"
                label="Sequence"
                type="number"
                value={form.callSequence}
                onChange={set('callSequence')}
                placeholder="5"
                disabled={!form.sequenceEnabled}
                inputProps={{ min: 1, style: { width: 150 } }}
                sx={{ width: 120, pointerEvents: form.sequenceEnabled ? 'auto' : 'none' }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
            <ProfileDateTimePicker
              label="Opens At"
              size="small"
              value={form.opensAt}
              onChange={set('opensAt')}
              disabled={form.alwaysOpen}
            />

            <ProfileDateTimePicker
              label="Deadline"
              size="small"
              value={form.closesAt}
              onChange={set('closesAt')}
              disabled={form.alwaysOpen}
              defaultTime="17:00"
            />

            <Box
              onClick={() => {
                if (!form.hasStage2 && !form.alwaysOpen)
                  setForm(f => ({ ...f, hasStage2: true }));
              }}
              sx={{ cursor: (!form.hasStage2 && !form.alwaysOpen) ? 'pointer' : 'default' }}
            >
              <ProfileDateTimePicker
                label="Stage 2 Deadline"
                size="small"
                value={form.deadline2}
                onChange={set('deadline2')}
                disabled={!form.hasStage2 || form.alwaysOpen}
                defaultTime="17:00"
              />
            </Box>
          </Box>




          {/* <TextField
            size="small"
            label="Min Partners (optional)"
            type="number"
            fullWidth
            value={form.minPartners}
            onChange={set('minPartners')}
            placeholder="1"
          /> */}

          {/* Additional Details */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1 }}>Additional Details</Typography>
          
          {/* <TextField
            size="small"
            label="Scope (optional)"
            fullWidth
            multiline
            rows={2}
            value={form.scope}
            onChange={set('scope')}
            placeholder="Brief description of the call scope"
          />
          
          <TextField
            size="small"
            label="Expected Outcomes (optional)"
            fullWidth
            multiline
            rows={2}
            value={form.expectedOutcomes}
            onChange={set('expectedOutcomes')}
            placeholder="What outcomes are expected from funded projects"
          />
          
          <TextField
            size="small"
            label="Eligibility Criteria (optional)"
            fullWidth
            multiline
            rows={2}
            value={form.eligibilityCriteria}
            onChange={set('eligibilityCriteria')}
            placeholder="Who is eligible to apply"
          />

          <TextField
            size="small"
            label="Submission URL (optional)"
            fullWidth
            value={form.submissionUrl}
            onChange={set('submissionUrl')}
            placeholder="https://..."
          />
 */}



   <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <Autocomplete
            size="small"
            options={actionOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.key === value.key}
            value={actionOptions.find(opt => opt.key === form.typeOfAction) || actionOptions.find(opt => opt.key === 'both')}
            onChange={(_, selected) => set('typeOfAction')(selected?.key || 'both')}
            renderInput={(params) => <TextField {...params} label="Type of Action" />}
          />


          <Autocomplete
            size="small"
            options={['upcoming', 'open', 'closed', 'cancelled']}
            value={form.status}
            onChange={(_, v) => set('status')(v || 'upcoming')}
            renderInput={(params) => <TextField {...params} label="Status" />}
          />

</Box>




{/* Budget & Requirements */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1 }}>Budget & Requirements</Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField
              size="small"
              label="Total Budget (optional)"
              type="number"
              value={form.totalBudget}
              onChange={set('totalBudget')}
              placeholder="1000000"
            />
            <TextField
              size="small"
              label="Max Per Project (optional)"
              type="number"
              value={form.maxPerProject}
              onChange={set('maxPerProject')}
              placeholder="200000"
            />
          </Box>


        </Box>




      )}

      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid()}
        >
          Create {kind === 'agency' ? 'Agency' : kind === 'program' ? 'Program' : 'Call'}
        </Button>
      </DialogActions>

      <Snackbar
        open={!!error}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>




    </Dialog>
  );
}


function QuickAccessPanel({
  links = [],
  onRemove = () => {},
  onEdit = () => {},
  onOpen = () => {},
  onAddCustom = () => {},
  catalog = { agencies: [], programs: [], calls: [] },
  onJumpAgency = () => {},
  onJumpProgram = () => {},
  onJumpCall = () => {},
  onCreateAgency = () => {},
  onCreateProgram = () => {},
  onCreateCall = () => {},
  contributions = [],
  onContributionSubmit = () => {},
  isSuperuser = false,
  onImportCalls = () => {},
  createCallButtonRef = null,
}) {
  const [filter, setFilter] = useState('all'); // all | agencies | programs | calls | custom
  const [contribDialog, setContribDialog] = useState({ open: false, kind: 'call' });
  const [error, setError] = useState(null);

  const filtered = useMemo(() => {
    if (filter === 'all') return links;
    // Map filter values to link types
    const typeMap = { 
      agencies: 'agency', 
      programs: 'program', 
      calls: 'call', 
      custom: 'custom' 
    };
    return links.filter((l) => l.type === (typeMap[filter] || filter));
  }, [links, filter]);

  const handleContributionSubmit = async (data) => {
    await onContributionSubmit(data);
    setContribDialog({ ...contribDialog, open: false });
  };

  return (
    <Sidebar elevation={0} sx={{ borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}`,
      boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }}>
      {/* Quick Jump & Create */}
      <Box sx={{ p: 2, pb: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          Quick Access
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pin items or jump directly using the dropdowns
        </Typography>

        {/* Agency Dropdown */}
        <Autocomplete
          size="small"
          options={catalog.agencies || []}
          getOptionLabel={(o) => o?.name || ''}
          onChange={(_, val) => val && onJumpAgency?.(val)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Jump to Agency"
              placeholder="e.g., NIH, ERC"
              InputProps={{ 
                ...params.InputProps, 
                startAdornment: <SearchIcon sx={{ mr: 1 }} /> 
              }}
            />
          )}
          sx={{ mb: 2 }}
        />

        {/* Program Dropdown */}
        <Autocomplete
          size="small"
          options={catalog.programs || []}
          groupBy={(o) => catalog.agencies?.find((a) => a.id === o.agencyId)?.name || 'Other'}
          getOptionLabel={(o) => o?.name || ''}
          onChange={(_, val) => val && onJumpProgram?.(val)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Jump to Program"
              placeholder="Horizon Europe, R01..."
              InputProps={{ 
                ...params.InputProps, 
                startAdornment: <SearchIcon sx={{ mr: 1 }} /> 
              }}
            />
          )}
          sx={{ mb: 2 }}
        />

        {/* Call Dropdown */}
        <Autocomplete
          size="small"
          options={catalog.calls || []}
          groupBy={(o) => catalog.programs?.find((p) => p.id === o.programId)?.name || 'Uncategorized'}
          getOptionLabel={(o) => o?.title || ''}
          onChange={(_, val) => val && onJumpCall?.(val)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Jump to Call"
              placeholder="Type call title"
              InputProps={{ 
                ...params.InputProps, 
                startAdornment: <SearchIcon sx={{ mr: 1 }} /> 
              }}
            />
          )}
          sx={{ mb: 1.5 }}
        />

        {/* Action Buttons */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0.75 }}>
          <Button
            ref={createCallButtonRef}
            variant="contained"
            size="medium"
            onClick={() => setContribDialog({ open: true, kind: 'call' })}
            fullWidth
            sx={{ fontSize: '1.1rem' }}
          >
            Create Call
          </Button>
          {isSuperuser && (
            <Button
              variant="outlined"
              size="medium"
              fullWidth
              startIcon={<AutoAwesomeIcon />}
              onClick={onImportCalls}
            >
              Import via AI
            </Button>
          )}
        </Box>
      </Box>

      {/* Submissions Section */}
      {contributions?.length > 0 && (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="overline" color="text.secondary">
              My submissions
            </Typography>
            <List dense>
              {contributions.map((c) => (
                <ListItem key={c.localId}>
                  <ListItemIcon>
                    {c.kind === 'agency' ? (
                      <BusinessIcon fontSize="small" />
                    ) : c.kind === 'program' ? (
                      <CategoryIcon fontSize="small" />
                    ) : (
                      <ListIcon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={c.title || c.name}
                    secondary={c.kind.charAt(0).toUpperCase() + c.kind.slice(1)}
                    primaryTypographyProps={{ noWrap: true }}
                  />
                  <Chip
                    size="small"
                    label={c.status}
                    color={
                      c.status === 'approved' 
                        ? 'success' 
                        : c.status === 'rejected' 
                        ? 'error' 
                        : 'warning'
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </>
      )}

      <Divider />

      {/* Pinned Links Section */}
      <Box sx={{ p: 1, pb: 1 }}>
        <Tabs 
          value={filter} 
          onChange={(_, v) => setFilter(v)} 
          variant="fullWidth" 
          textColor="primary" 
          indicatorColor="primary"
        >
          <Tab label="All" value="all" />
          <Tab label="Agencies" value="agencies" />
          <Tab label="Programs" value="programs" />
          <Tab label="Calls" value="calls" />
        </Tabs>
      </Box>

      <Box sx={{ flex: '0 1 auto', minHeight: 0, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No quick links yet. Pin a program or call to get started.
            </Typography>
          </Box>
        ) : (
          <List dense>
            {filtered.map((l) => (
              <ListItem 
                key={(l.id ?? l.href) + l.type} 
                button 
                onClick={() => onOpen(l)}
              >
                <ListItemIcon>
                  {l.type === 'agency' ? (
                    <BusinessIcon fontSize="small" />
                  ) : l.type === 'program' ? (
                    <CategoryIcon fontSize="small" />
                  ) : l.type === 'call' ? (
                    <ListIcon fontSize="small" />
                  ) : (
                    <LinkIcon fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={l.title}
                  secondary={l.subtitle}
                  primaryTypographyProps={{ noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Remove">
                    <IconButton 
                      edge="end" 
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(l.id ?? l.href);
                      }}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Contribution Dialog */}
      <ContributionDialog
        open={contribDialog.open}
        kind={contribDialog.kind}
        catalog={catalog}
        onClose={() => setContribDialog({ ...contribDialog, open: false })}
        onSubmit={handleContributionSubmit}
      />

      {/* Error Snackbar */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setError(null)} 
          severity="error" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Sidebar>
  );
}

export default QuickAccessPanel;
